import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { supabaseMarketData, isMarketDataConfigured } from '@/lib/supabase-market-data'
import { captureError } from '@/lib/sentry'
import { fetchAllRows } from '@/lib/supabase-paginate'
import { fetchDbcConfig } from '@/lib/dbcConfig'
import {
  getRewardsVaultBalance,
  fetchGlobalState,
  fetchStakerCount,
} from '@/lib/solana'
import { DEXTORO_EFFECTIVE_RATE_GRADUATED } from '@/lib/constants/feeShares'

// Meteora's fixed 20% protocol cut on BC trading fees, taken before LP split.
// dextoro's protocolTradingShareBC is the share of the REMAINING 80% LP pool,
// not of total fees. Applying protocolShareBC directly overstates revenue.
const METEORA_BC_FEE_SHARE = 0.20

// DAMM V2 effective rate: 2% * 80% LP * 30% dextoro share. Centralised in feeShares.ts.
const DAMM_V2_DEXTORO_EFFECTIVE_RATE = DEXTORO_EFFECTIVE_RATE_GRADUATED

export interface StakingHealth {
  totalDtrStaked: number
  uniqueStakers: number | null // null when getProgramAccounts unavailable
  currentEpoch: number
  currentEpochStatus: string
  totalSolDistributed: number
  currentEpochRevenue: number
  estimatedNextPayout: number
  stakingApy: number | null // null = "Coming Soon"
  rewardsVaultBalanceSOL: number
}

/**
 * Fetch DTR price in SOL from CoinGecko (DTR is listed as "dextoro").
 */
async function fetchDtrPriceInSol(): Promise<number> {
  try {
    const res = await fetch(
      'https://api.coingecko.com/api/v3/simple/price?ids=dextoro&vs_currencies=sol'
    )
    if (res.ok) {
      const data = await res.json()
      const price = data?.dextoro?.sol ?? 0
      if (price > 0) return price
    }
  } catch (error) {
    captureError(error instanceof Error ? error : new Error('useStakingHealth: CoinGecko DTR price fetch failed'), {
      feature: 'admin_dashboard',
      component: 'useStakingHealth',
    })
  }
  return 0
}

/**
 * Calculate protocol revenue from the trades table (fallback when protocol_revenue is empty).
 * Same logic as dextoro-launch/src/hooks/useStakingKPIs.ts
 */
async function calculateRevenueFromTrades(): Promise<{ weekly: number; total: number }> {
  if (!isMarketDataConfigured() || !supabaseMarketData) {
    return { weekly: 0, total: 0 }
  }

  // Fetch on-chain fee config
  let tradingFeeRate = 0.01
  let protocolShareBC = 0.70
  try {
    const config = await fetchDbcConfig()
    tradingFeeRate = config.tradingFeeRate
    protocolShareBC = config.protocolTradingShareBC
  } catch {
    // Continue with defaults
  }

  // Fetch all tokens with both DBC and DAMM V2 pool addresses so we can
  // classify each trade by pool-address membership. The market-data indexer
  // writes trade.pool_address as either the DBC pool (pre-graduation) or
  // the DAMM V2 pool (post-graduation); see dextoro-market-data
  // src/indexer/index.ts where tradePoolAddress is resolved.
  const { data: tokens } = await supabase
    .from('launched_tokens')
    .select('pool_address, damm_pool_address')

  const dbcPools = new Set<string>()
  const dammV2Pools = new Set<string>()
  tokens?.forEach((t: { pool_address: string | null; damm_pool_address: string | null }) => {
    if (t.pool_address) dbcPools.add(t.pool_address)
    if (t.damm_pool_address) dammV2Pools.add(t.damm_pool_address)
  })

  // Fetch all trades from market-data with pagination.
  // Supabase returns max 1000 rows by default; fetchAllRows paginates to get all.
  const { data: allTrades } = await fetchAllRows(supabaseMarketData!, {
    table: 'trades',
    select: 'quote_amount, pool_address, block_time, created_at',
  })

  if (!allTrades?.length) return { weekly: 0, total: 0 }

  const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
  // BC multiplier: tradingFeeRate * (1 - METEORA_BC_FEE_SHARE) * protocolShareBC
  // = 0.01 * 0.80 * 0.70 = 0.0056 (0.56% of volume with default config).
  // Matches useProtocolRevenue.ts dextoroMultiplierBC composition.
  const lpShareBC = 1 - METEORA_BC_FEE_SHARE
  const dextoroMultiplierBC = tradingFeeRate * lpShareBC * protocolShareBC

  let totalRevenue = 0
  let weeklyRevenue = 0

  allTrades.forEach((trade: Record<string, unknown>) => {
    const solAmount = Number(trade.quote_amount || 0)
    if (solAmount <= 0) return

    const poolAddress = trade.pool_address as string | undefined
    if (!poolAddress) return

    // Classify by pool address membership. Trades for pools that aren't
    // dextoro launched tokens shouldn't inflate protocol revenue estimates.
    let protocolFee: number
    if (dammV2Pools.has(poolAddress)) {
      protocolFee = solAmount * DAMM_V2_DEXTORO_EFFECTIVE_RATE
    } else if (dbcPools.has(poolAddress)) {
      protocolFee = solAmount * dextoroMultiplierBC
    } else {
      return
    }

    const blockTimeRaw = trade.block_time || trade.created_at
    const tradeDate = blockTimeRaw ? new Date(blockTimeRaw as string) : new Date()

    totalRevenue += protocolFee
    if (tradeDate >= sevenDaysAgo) {
      weeklyRevenue += protocolFee
    }
  })

  return { weekly: weeklyRevenue, total: totalRevenue }
}

export function useStakingHealth() {
  return useQuery({
    queryKey: ['staking-health'],
    queryFn: async (): Promise<StakingHealth> => {
      // Fetch all data sources in parallel
      const [
        rewardsVaultBalanceSOL,
        globalState,
        stakerCount,
        tradeRevenue,
        dtrPrice,
      ] = await Promise.all([
        getRewardsVaultBalance(),
        fetchGlobalState(),
        fetchStakerCount(),
        calculateRevenueFromTrades(),
        fetchDtrPriceInSol(),
      ])

      // On-chain staking metrics
      const totalDtrStaked = globalState?.totalStaked ?? 0
      const totalSolDistributed = globalState?.totalRewardsDistributed ?? 0
      const onChainEpoch = globalState?.epochCount ?? 0

      // Supabase epoch as secondary source
      let supabaseEpoch = 0
      let currentEpochStatus = 'active'
      if (isMarketDataConfigured() && supabaseMarketData) {
        try {
          const { data: latestEpoch } = await supabaseMarketData
            .from('staking_epochs')
            .select('epoch_number, status')
            .in('status', ['active', 'funded'])
            .order('epoch_number', { ascending: false })
            .limit(1)

          if (latestEpoch?.[0]) {
            const ep = latestEpoch[0] as Record<string, unknown>
            supabaseEpoch = Number(ep.epoch_number || 0)
            currentEpochStatus = (ep.status as string) || 'active'
          }
        } catch (error) {
          captureError(error instanceof Error ? error : new Error('useStakingHealth: staking epochs query failed'), {
            feature: 'admin_dashboard',
            component: 'useStakingHealth',
          })
        }
      }

      const currentEpoch = Math.max(onChainEpoch, supabaseEpoch)

      // Revenue: weekly from trades is the "current epoch revenue"
      const currentEpochRevenue = tradeRevenue.weekly
      const estimatedNextPayout = currentEpochRevenue * 0.5

      // APY: (weeklyStakerShare * 52) / (totalStakedDTR * dtrPriceInSOL) * 100
      let stakingApy: number | null = null
      if (currentEpochRevenue > 0 && totalDtrStaked > 0 && dtrPrice > 0) {
        const annualStakerShare = currentEpochRevenue * 0.5 * 52
        const totalStakedValueSol = totalDtrStaked * dtrPrice
        stakingApy = (annualStakerShare / totalStakedValueSol) * 100
      }

      return {
        totalDtrStaked,
        uniqueStakers: stakerCount,
        currentEpoch,
        currentEpochStatus,
        totalSolDistributed,
        currentEpochRevenue,
        estimatedNextPayout,
        stakingApy,
        rewardsVaultBalanceSOL,
      }
    },
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  })
}
